import React, { useCallback, useEffect } from 'react';
import ReactFlow, {
  Background,
  NodeTypes,
  OnNodesChange,
  OnEdgesChange,
  OnNodeClick,
  OnNodeDoubleClick,
  Node,
  Edge,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import styled, { useTheme } from 'styled-components';
import { ScriptNode } from './ScriptNode';
import type { ScriptNodeData } from './types';

// Registrar tipo de nó customizado
const nodeTypes: NodeTypes = {
  scriptNode: ScriptNode,
};

const FlowContainer = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};

  .react-flow__node {
    cursor: pointer;
  }

  .react-flow__edge-path {
    stroke-width: 2;
  }

  .react-flow__edge.animated .react-flow__edge-path {
    stroke-dasharray: 8, 8;
    animation: dashdraw 1s linear infinite;
  }

  .react-flow__edge.selected .react-flow__edge-path {
    stroke-width: 3;
    filter: drop-shadow(0 0 4px currentColor);
  }

  @keyframes dashdraw {
    to {
      stroke-dashoffset: -16;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textSecondary};
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

interface FlowCanvasProps {
  nodes: Node<ScriptNodeData>[];
  edges: Edge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onNodeClick?: OnNodeClick;
  onNodeDoubleClick?: OnNodeDoubleClick;
}

/**
 * Inner component to access useReactFlow hook
 */
const FlowCanvasInner: React.FC<FlowCanvasProps> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onNodeClick,
  onNodeDoubleClick,
}) => {
  const theme = useTheme();
  const { setCenter, fitView } = useReactFlow();
  const hasInitialized = React.useRef(false);

  // Centralizar no nó "saudacao" APENAS na primeira carga
  useEffect(() => {
    if (nodes.length > 0 && !hasInitialized.current) {
      hasInitialized.current = true;

      // Procurar pelo nó "saudacao" (conteudo-saudacao ou mentalidade-saudacao)
      const saudacaoNode = nodes.find(
        (node) =>
          node.id === 'conteudo-saudacao' ||
          node.id === 'mentalidade-saudacao' ||
          node.data?.scriptName?.toLowerCase().includes('saudacao')
      );

      if (saudacaoNode) {
        // Centralizar no nó saudacao com zoom 0.9 (melhor visualização)
        setTimeout(() => {
          setCenter(
            saudacaoNode.position.x + 110, // largura do nó / 2
            saudacaoNode.position.y + 60, // altura do nó / 2
            { zoom: 0.9, duration: 800 }
          );
        }, 100);
      } else {
        // Se não encontrar saudacao, usar fitView com zoom maior
        setTimeout(() => {
          fitView({ padding: 0.2, maxZoom: 0.8, duration: 800 });
        }, 100);
      }
    }
  }, [nodes, setCenter, fitView]);

  // Configurações padrão para edges (não usado - cada edge tem seu estilo)
  const defaultEdgeOptions = {
    type: 'smoothstep',
    animated: false,
    style: {
      strokeWidth: 2,
    },
  };

  if (nodes.length === 0) {
    return (
      <FlowContainer>
        <EmptyState>
          <h3>Nenhum script encontrado</h3>
          <p>
            Configure seus scripts nas configurações do bot<br />
            ou gere novos scripts com IA na aba "IA Generator"
          </p>
        </EmptyState>
      </FlowContainer>
    );
  }

  return (
    <FlowContainer>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView={false}
        minZoom={0.3}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
      >
        {/* Background com pontos maiores */}
        <Background
          variant="dots"
          gap={24}
          size={2}
          color={theme.colors.border}
        />
      </ReactFlow>
    </FlowContainer>
  );
};

/**
 * Canvas do React Flow para renderizar nós de scripts
 * Com background, controles e minimap customizados
 * Foco inicial no nó "saudacao" com zoom otimizado
 */
export const FlowCanvas: React.FC<FlowCanvasProps> = (props) => {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner {...props} />
    </ReactFlowProvider>
  );
};
